/**
 * Resolución unificada de oferta internacional por país:
 * playback Mux + precio VIP Stripe + descuento 1.er mes.
 *
 * Usado por DualCTAVideoLanding, InvitacionExclusivaMux y admin preview.
 *
 * Checkout cobra un Stripe Price ID real (alineado con create-subscription-checkout):
 *   1) país con `stripePriceId` en admin → ese Price (+ promo si applyDiscount)
 *   2) else → oferta global canónica para **todos** los países:
 *      `FALLBACK_VIP_STRIPE_PRICE_ID` + `FALLBACK_VIP_STRIPE_PROMOTION_CODE_ID`
 *      (ANA70 70% 1.er mes). Display: $29.99 → ~$9.00.
 *      El mapa admin vacío ya no deja a nadie sin descuento real.
 *
 * @see CONFIG_PIPELINE_DUAL_CTA / DUAL_CTA_VIP_CHARGE_PRIORITY in configPipelines.ts
 */

import { getPlansForCurrency } from "@/config/bioTemplatePremiumPlans";
import {
  isValidStripePriceId,
  isValidStripePromotionCodeId,
  resolveInternationalHomeVipPricingForCountry,
  resolveVipFirstChargeCents,
  type InternationalHomeVipPricingByCountry,
} from "@/lib/internationalHomeTemplate";
import {
  resolveInternationalHomeMuxPlaybackId,
  resolveInternationalHomeMuxPlaybackSource,
  type InternationalHomeMuxPlaybackSource,
  type ResolveInternationalHomeMuxPlaybackOpts,
} from "@/lib/resolveInternationalHomeMuxPlayback";

export type InternationalHomeVipPriceSource =
  | "country"
  | "vip_default"
  | "premium_invite"
  | "membership_catalog"
  | "env_default"
  | "fallback";

/**
 * Fallback display + checkout IDs — re-export canonical VIP defaults
 * (ANA70 / ana_vip_70 · 70% once). Server twin: api/lib/vipMembershipStripeDefaults.
 */
export {
  VIP_STRIPE_PRICE_ID as FALLBACK_VIP_STRIPE_PRICE_ID,
  VIP_STRIPE_PROMOTION_CODE_ID as FALLBACK_VIP_STRIPE_PROMOTION_CODE_ID,
  VIP_LIST_PRICE_USD as FALLBACK_VIP_DISPLAY_PRICE_USD,
  VIP_FIRST_MONTH_DISCOUNT_PCT as FALLBACK_VIP_DISPLAY_DISCOUNT_PCT,
  VIP_STRIPE_PRICE_ID,
  VIP_STRIPE_PROMOTION_CODE_ID,
  VIP_STRIPE_PROMOTION_CODE,
  VIP_STRIPE_COUPON_ID,
  VIP_FIRST_MONTH_DISCOUNT_PCT,
  VIP_LIST_PRICE_CENTS,
  vipFirstMonthChargeCents,
} from "@/lib/vipMembershipStripeDefaults";

export type InternationalHomeVipCheckoutPricing = {
  currency: "USD";
  fullPriceUsd: number;
  fullPriceCents: number;
  amountCents: number;
  discountPct: number;
  productName: string;
  /**
   * @deprecated Prefer `stripePriceId`. Kept so DualCTA/analytics that still
   * read `adminPriceSet` keep working during the migration — true when we
   * send an explicit Stripe Price (country or fallback), never for inline
   * amount-based create.
   */
  adminPriceSet: boolean;
  priceSource: InternationalHomeVipPriceSource;
  /** Stripe Price ID that checkout must charge. */
  stripePriceId: string;
  /** Stripe Promotion Code ID when a discount should be auto-applied. */
  promotionCodeId: string | null;
  /** true when `stripePriceId` came from the env fallback (not country admin). */
  usingFallbackPrice: boolean;
};

export type InternationalHomeOffer = {
  country: string | null;
  playbackId: string;
  playbackSource: InternationalHomeMuxPlaybackSource;
  pricing: InternationalHomeVipCheckoutPricing;
};

export type ResolveInternationalHomeVipCheckoutPricingOpts = {
  country?: string | null;
  vipPricingByCountry?: InternationalHomeVipPricingByCountry | null;
  /**
   * Compact public API field: when all countries share one Price/promo,
   * site-settings ships this instead of 200× duplicated map (4G AR win).
   */
  vipPricingDefault?: import("@/lib/internationalHomeTemplate").InternationalHomeVipPricing | null;
  /** `vip_price_usd` desde premium-invite-config (display hint only). */
  runtimeVipPriceUsd?: number | null;
  language?: string;
  defaultProductName?: string;
  defaultFullPriceUsd?: number;
};

export function resolveInternationalHomeVipCheckoutPricing(
  opts: ResolveInternationalHomeVipCheckoutPricingOpts,
): InternationalHomeVipCheckoutPricing {
  const lang =
    opts.language ||
    (typeof navigator !== "undefined" && navigator.language
      ? navigator.language
      : "en");

  const { vipPlan } = getPlansForCurrency("USD", lang);
  const countryOnly = resolveInternationalHomeVipPricingForCountry(
    opts.country,
    opts.vipPricingByCountry,
  );
  const usedDefault =
    !countryOnly &&
    Boolean(
      opts.vipPricingDefault &&
        typeof opts.vipPricingDefault.fullPriceUsd === "number" &&
        opts.vipPricingDefault.fullPriceUsd > 0,
    );
  const countryPricing =
    countryOnly ||
    (usedDefault ? opts.vipPricingDefault! : null);

  const productName =
    opts.defaultProductName || vipPlan?.productName || "VIP Membership";

  // Chargeable admin entry (country override or compact default): valid stripePriceId.
  const countryPriceId =
    countryPricing?.stripePriceId && isValidStripePriceId(countryPricing.stripePriceId)
      ? countryPricing.stripePriceId.trim()
      : null;

  if (countryPriceId) {
    const applyDiscount =
      countryPricing!.applyDiscount === true &&
      Boolean(countryPricing!.promotionCodeId) &&
      isValidStripePromotionCodeId(countryPricing!.promotionCodeId);

    const fullPriceUsd = countryPricing!.fullPriceUsd;
    const discountPct = applyDiscount
      ? countryPricing!.firstMonthDiscountPct &&
        countryPricing!.firstMonthDiscountPct > 0
        ? countryPricing!.firstMonthDiscountPct
        : 0
      : 0;

    const { fullPriceCents, chargeCents } = resolveVipFirstChargeCents(
      fullPriceUsd,
      discountPct > 0 ? discountPct : undefined,
    );

    return {
      currency: "USD",
      fullPriceUsd,
      fullPriceCents,
      amountCents: chargeCents,
      discountPct,
      productName,
      adminPriceSet: true,
      priceSource: usedDefault ? "vip_default" : "country",
      stripePriceId: countryPriceId,
      promotionCodeId: applyDiscount
        ? countryPricing!.promotionCodeId!.trim()
        : null,
      usingFallbackPrice: false,
    };
  }

  // No per-country chargeable Price → global canonical offer for every country.
  // Same Price + ANA70 promo as Railway STRIPE_FALLBACK_* defaults so the
  // DualCTA map can be empty and visitors still get real 1st-month discount.
  // Display USD can still follow country fullPriceUsd / premium-invite hint.
  const runtimeUsd =
    typeof opts.runtimeVipPriceUsd === "number" &&
    Number.isFinite(opts.runtimeVipPriceUsd) &&
    opts.runtimeVipPriceUsd > 0
      ? opts.runtimeVipPriceUsd
      : null;
  const displayFullUsd =
    countryPricing && countryPricing.fullPriceUsd > 0
      ? countryPricing.fullPriceUsd
      : runtimeUsd ?? FALLBACK_VIP_DISPLAY_PRICE_USD;

  const discountPct =
    countryPricing?.applyDiscount === true &&
    countryPricing.firstMonthDiscountPct &&
    countryPricing.firstMonthDiscountPct > 0
      ? countryPricing.firstMonthDiscountPct
      : FALLBACK_VIP_DISPLAY_DISCOUNT_PCT;

  // Prefer country promo if admin set applyDiscount+promo without price_ (rare);
  // otherwise the brand ANA70 promotion code.
  const countryPromo =
    countryPricing?.applyDiscount === true &&
    countryPricing.promotionCodeId &&
    isValidStripePromotionCodeId(countryPricing.promotionCodeId)
      ? countryPricing.promotionCodeId.trim()
      : null;

  const { fullPriceCents, chargeCents } = resolveVipFirstChargeCents(
    displayFullUsd,
    discountPct,
  );

  return {
    currency: "USD",
    fullPriceUsd: displayFullUsd,
    fullPriceCents,
    amountCents: chargeCents,
    discountPct,
    productName,
    adminPriceSet: true,
    priceSource: "fallback",
    stripePriceId: FALLBACK_VIP_STRIPE_PRICE_ID,
    promotionCodeId: countryPromo || FALLBACK_VIP_STRIPE_PROMOTION_CODE_ID,
    usingFallbackPrice: true,
  };
}

export type FormatInternationalHomeVipPriceDisplayOpts = {
  pricing: InternationalHomeVipCheckoutPricing;
  /** `(formatted) => "$X / month"` */
  pricePerMonth: (formatted: string) => string;
  language?: string;
};

export function formatInternationalHomeVipPriceDisplay(
  opts: FormatInternationalHomeVipPriceDisplayOpts,
): {
  originalPriceLine: string | null;
  priceLine: string;
  priceSubline: string | null;
} {
  const { pricing, pricePerMonth } = opts;

  const fullFormatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(pricing.fullPriceUsd);

  const chargeFormatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(pricing.amountCents / 100);

  if (pricing.discountPct > 0) {
    // Precio de atracción: monto bajo sin decir “primer mes” (el cupón se aplica en Stripe).
    return {
      originalPriceLine: fullFormatted,
      priceLine: chargeFormatted,
      priceSubline: null,
    };
  }

  return {
    originalPriceLine: null,
    priceLine: pricePerMonth(chargeFormatted),
    priceSubline: null,
  };
}

export type ResolveInternationalHomeOfferOpts =
  ResolveInternationalHomeMuxPlaybackOpts &
    ResolveInternationalHomeVipCheckoutPricingOpts;

export function resolveInternationalHomeOffer(
  opts: ResolveInternationalHomeOfferOpts,
): InternationalHomeOffer {
  const playbackId = resolveInternationalHomeMuxPlaybackId(opts);
  const playbackSource = resolveInternationalHomeMuxPlaybackSource(playbackId, opts);
  const pricing = resolveInternationalHomeVipCheckoutPricing(opts);

  const code =
    typeof opts.country === "string" ? opts.country.trim().toUpperCase().slice(0, 2) : null;

  return {
    country: code || null,
    playbackId,
    playbackSource,
    pricing,
  };
}
